import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import type { ComponentChildren } from 'preact'
import { api } from '../api/client'
import type { Assignment } from '../api/generated/Assignment'
import type { NewShiftAssignment } from '../api/generated/NewShiftAssignment'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import type { Volunteer } from '../api/generated/Volunteer'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { assignedShifts } from '../assignments/schedule'
import { StatusChip, volunteerName } from '../assignments/status'
import { useEvent } from '../events/EventContext'
import { useOrganizerData } from '../organizer/OrganizerData'
import { planningDays } from '../planning/grid'
import { AnswerList } from '../questions/AnswerList'
import { formatDay, formatRange } from '../shifts/time'
import { availabilityForm, describeDay } from './availability'
import { WishedPositionChip } from './chips'

export type VolunteerRef = Pick<Volunteer, 'user_id' | 'display_name' | 'email' | 'phone'>

const suggestionMessages = {
  not_found: "Ce créneau n'existe plus.",
}

function Section({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <Stack spacing={1}>
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
      {children}
    </Stack>
  )
}

function Availability({ volunteer, shifts }: { volunteer: Volunteer; shifts: Shift[] }) {
  const { event } = useEvent()
  if (volunteer.availability.length === 0)
    return <Typography color="text.secondary">Pas encore indiquées.</Typography>
  const days = planningDays(shifts, event)
  const form = availabilityForm(volunteer.availability, days)
  return (
    <List dense disablePadding>
      {days.map((day) => (
        <ListItem key={day} disableGutters>
          <ListItemText primary={formatDay(day)} secondary={describeDay(form[day])} />
        </ListItem>
      ))}
    </List>
  )
}

/** Shifts the volunteer could take, those of their positions of interest first, to assign them. */
function Suggestions({
  slug,
  volunteer,
  shifts,
  positions,
  onAssigned,
}: {
  slug: string
  volunteer: Volunteer
  shifts: Shift[]
  positions: Position[]
  onAssigned: (assignments: Assignment[]) => void
}) {
  const suggestions = useApi<string[]>(
    `/events/${slug}/volunteers/${volunteer.user_id}/suggestions`,
  )
  const { error, pending, run } = useAction(suggestionMessages)
  const shiftById = new Map(shifts.map((shift) => [shift.id, shift]))
  const assign = (shiftId: string) =>
    run(async () => {
      const body: NewShiftAssignment = { user_id: volunteer.user_id }
      onAssigned(
        await api.post<Assignment[]>(`/events/${slug}/shifts/${shiftId}/assignments`, body),
      )
      suggestions.reload()
    })
  if (suggestions.error) return <Alert severity="error">{suggestions.error}</Alert>
  if (!suggestions.data) return <CircularProgress />
  if (suggestions.data.length === 0)
    return (
      <Typography color="text.secondary">
        Aucun créneau à pourvoir dans ses disponibilités.
      </Typography>
    )
  return (
    <>
      <List dense disablePadding>
        {suggestions.data.flatMap((id) => {
          const shift = shiftById.get(id)
          const position = shift && positions.find((p) => p.id === shift.position_id)
          if (!shift || !position) return []
          return [
            <ListItem key={id} disableGutters sx={{ gap: 1, flexWrap: 'wrap' }}>
              <ListItemText
                primary={position.name}
                secondary={formatRange(shift.starts_at, shift.ends_at)}
              />
              {volunteer.position_ids.includes(position.id) && <WishedPositionChip />}
              <Button size="small" disabled={pending} onClick={() => assign(id)}>
                Affecter
              </Button>
            </ListItem>,
          ]
        })}
      </List>
      {error && <Alert severity="error">{error}</Alert>}
    </>
  )
}

/**
 * What organizers know of a volunteer: contact, availability, positions of interest, shifts in
 * shifts to assign them to, shifts in every status, answers.
 */
export function VolunteerCard({
  slug,
  volunteer: person,
  onChange,
  onClose,
}: {
  slug: string
  volunteer: VolunteerRef
  /** Receives all the event's assignments after assigning the volunteer. */
  onChange?: (assignments: Assignment[]) => void
  onClose: () => void
}) {
  const { shifts, volunteers } = useOrganizerData()
  const positions = useApi<Position[]>(`/events/${slug}/positions`)
  const assignments = useApi<Assignment[]>(`/events/${slug}/assignments`)
  const volunteer = volunteers.data?.find((v) => v.user_id === person.user_id)
  const error = positions.error ?? assignments.error ?? volunteers.error
  const own = assignments.data?.filter((a) => a.user_id === person.user_id)
  const rows = own && assignedShifts(own, shifts.data ?? [], positions.data ?? [])
  const wished = positions.data?.filter((p) => volunteer?.position_ids.includes(p.id))
  const assigned = (all: Assignment[]) => {
    assignments.setData(all)
    onChange?.(all)
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{volunteerName(person)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            {person.email && <Link href={`mailto:${person.email}`}>{person.email}</Link>}
            {person.phone && <Link href={`tel:${person.phone}`}>{person.phone}</Link>}
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {!volunteers.data && <CircularProgress />}
          {volunteer && (
            <>
              <Section title="Disponibilités">
                <Availability volunteer={volunteer} shifts={shifts.data ?? []} />
              </Section>
              <Section title="Postes souhaités">
                {wished && (
                  <Typography color={wished.length === 0 ? 'text.secondary' : undefined}>
                    {wished.length === 0
                      ? 'Aucun poste indiqué.'
                      : wished.map((p) => p.name).join(', ')}
                  </Typography>
                )}
              </Section>
            </>
          )}
          {volunteer && shifts.data && positions.data && (
            <Section title="Affecter à un créneau">
              <Suggestions
                slug={slug}
                volunteer={volunteer}
                shifts={shifts.data}
                positions={positions.data}
                onAssigned={assigned}
              />
            </Section>
          )}
          <Section title="Créneaux">
            {!error && !rows && <CircularProgress />}
            {rows?.length === 0 && <Typography color="text.secondary">Aucun créneau.</Typography>}
            <List dense disablePadding>
              {rows?.map(({ assignment, shift, position }) => (
                <ListItem
                  key={assignment.id}
                  disableGutters
                  secondaryAction={<StatusChip status={assignment.status} />}
                >
                  <ListItemText
                    primary={position.name}
                    secondary={formatRange(shift.starts_at, shift.ends_at)}
                  />
                </ListItem>
              ))}
            </List>
          </Section>
          <Section title="Réponses">
            <AnswerList slug={slug} userId={person.user_id} />
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
